import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useAuth } from '../../providers/SupabaseProvider';

interface BeforeInstallPromptEvent extends Event {
  readonly platforms: string[];
  readonly userChoice: Promise<{ outcome: 'accepted' | 'dismissed'; platform: string }>;
  prompt: () => Promise<void>;
}

type PlatformType = 'ios' | 'android' | 'desktop' | 'unknown';

const DISMISSED_KEY = 'pwa-install-dismissed';
const INSTALLED_KEY = 'pwa-installed';

const isStandaloneMode = () => {
  if (typeof window === 'undefined') {
    return false;
  }

  const isStandaloneMatch = window.matchMedia('(display-mode: standalone)').matches;
  const isNavigatorStandalone = (window.navigator as unknown as { standalone?: boolean }).standalone === true;

  return isStandaloneMatch || isNavigatorStandalone;
};

const detectPlatform = (): PlatformType => {
  if (typeof window === 'undefined') {
    return 'unknown';
  }

  const ua = window.navigator.userAgent.toLowerCase();

  if (/iphone|ipad|ipod/.test(ua)) {
    return 'ios';
  }

  if (/android/.test(ua)) {
    return 'android';
  }

  if (/windows|macintosh|linux/.test(ua)) {
    return 'desktop';
  }

  return 'unknown';
};

const getInstructions = (platform: PlatformType, canTriggerInstall: boolean) => {
  switch (platform) {
    case 'ios':
      return 'Tippen Sie im Safari-Browser auf das Teilen-Symbol und wählen Sie "Zum Home-Bildschirm", um Absticare zu installieren.';
    case 'android':
      return canTriggerInstall
        ? 'Installieren Sie Absticare für schnellen Zugriff direkt vom Startbildschirm.'
        : 'Öffnen Sie das Browser-Menü (⋮) und wählen Sie "Zum Startbildschirm hinzufügen", um Absticare zu installieren.';
    case 'desktop':
      return 'Installieren Sie Absticare über das Browser-Menü und wählen Sie "Installieren" bzw. "Zum Startbildschirm hinzufügen".';
    default:
      return 'Installieren Sie Absticare als App über das Browser-Menü, um schneller darauf zugreifen zu können.';
  }
};

export const AddToHomeScreenPrompt: React.FC = () => {
  const { user } = useAuth();
  const [shouldShow, setShouldShow] = useState(false);
  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null);
  const [platform, setPlatform] = useState<PlatformType>('unknown');

  const dismissPrompt = useCallback(() => {
    localStorage.setItem(DISMISSED_KEY, 'true');
    setShouldShow(false);
  }, []);

  const markAsInstalled = useCallback(() => {
    localStorage.setItem(INSTALLED_KEY, 'true');
    setShouldShow(false);
  }, []);

  const canTriggerInstall = useMemo(() => Boolean(deferredPrompt), [deferredPrompt]);

  const instructions = useMemo(
    () => getInstructions(platform, canTriggerInstall),
    [platform, canTriggerInstall]
  );

  useEffect(() => {
    if (typeof window === 'undefined') {
      return undefined;
    }

    // Der Installationshinweis soll nur angemeldeten Nutzern angezeigt werden.
    if (!user) {
      setShouldShow(false);
      setDeferredPrompt(null);
      return undefined;
    }

    const dismissed = localStorage.getItem(DISMISSED_KEY) === 'true';
    const installed = localStorage.getItem(INSTALLED_KEY) === 'true';

    const platformType = detectPlatform();
    setPlatform(platformType);

    if (isStandaloneMode()) {
      markAsInstalled();
      return undefined;
    }

    if (!dismissed && !installed && platformType === 'ios') {
      setShouldShow(true);
    }

    const handleBeforeInstallPrompt = (event: Event) => {
      const beforeInstallEvent = event as BeforeInstallPromptEvent;
      beforeInstallEvent.preventDefault();
      setDeferredPrompt(beforeInstallEvent);

      if (!dismissed && !installed && !isStandaloneMode()) {
        setShouldShow(true);
      }
    };

    const handleAppInstalled = () => {
      markAsInstalled();
    };

    const mediaQuery = window.matchMedia('(display-mode: standalone)');
    const handleDisplayModeChange = (event: MediaQueryListEvent) => {
      if (event.matches) {
        markAsInstalled();
      }
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    window.addEventListener('appinstalled', handleAppInstalled);

    if ('addEventListener' in mediaQuery) {
      mediaQuery.addEventListener('change', handleDisplayModeChange);
    } else {
      mediaQuery.addListener(handleDisplayModeChange);
    }

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible' && isStandaloneMode()) {
        markAsInstalled();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
      window.removeEventListener('appinstalled', handleAppInstalled);
      document.removeEventListener('visibilitychange', handleVisibilityChange);

      if ('removeEventListener' in mediaQuery) {
        mediaQuery.removeEventListener('change', handleDisplayModeChange);
      } else {
        mediaQuery.removeListener(handleDisplayModeChange);
      }
    };
  }, [markAsInstalled, user]);

  const handleInstall = useCallback(async () => {
    if (!deferredPrompt) {
      return;
    }

    try {
      await deferredPrompt.prompt();
      const choice = await deferredPrompt.userChoice;
      if (choice.outcome === 'accepted') {
        markAsInstalled();
      }
    } catch (error) {
      console.warn('Installation prompt failed', error);
    } finally {
      setDeferredPrompt(null);
    }
  }, [deferredPrompt, markAsInstalled]);

  if (!user || !shouldShow) {
    return null;
  }

  return (
    <div className="fixed bottom-4 right-4 max-w-sm rounded-lg bg-white dark:bg-gray-800 shadow-lg border border-gray-200 dark:border-gray-700 p-4 z-40">
      <div className="flex items-start gap-3">
        <div className="flex-1">
          <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">Absticare zum Startbildschirm hinzufügen</h3>
          <p className="mt-2 text-sm text-gray-600 dark:text-gray-300">{instructions}</p>
        </div>
        <button
          type="button"
          onClick={dismissPrompt}
          aria-label="Hinweis schließen"
          className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
        >
          &times;
        </button>
      </div>
      <div className="mt-4 flex justify-end gap-2">
        <button
          type="button"
          onClick={dismissPrompt}
          className="px-3 py-1.5 text-sm rounded-md border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700"
        >
          Später
        </button>
        {/* Installations-Button auf allen unterstützten Nicht-iOS-Plattformen anzeigen, sobald der Browser das Prompt liefert. */}
        {platform !== 'ios' && canTriggerInstall && (
          <button
            type="button"
            onClick={handleInstall}
            className="px-3 py-1.5 text-sm rounded-md bg-primary-600 text-white hover:bg-primary-700"
          >
            Installieren
          </button>
        )}
      </div>
    </div>
  );
};
